import FusionCharts from 'fusioncharts'
import charts from 'fusioncharts/fusioncharts.charts'
import ReactFusioncharts from 'react-fusioncharts'
import React, { useRef, useState } from 'react'
import { ChartType, MSLineProps, Series } from './MSLine.types'
import { IconBarChart, IconLineChart } from 'assets'
import styles from './MSLine.module.scss'
import cl from 'classnames'
import { dataSource, useCategoryToolTipProps } from './MSLine.utils'
import { ChartLegend } from './ChartLegend'
import { Button, Text1 } from 'components/shared'
import ReactDOM from 'react-dom'
import { ToolTipContent } from './ToolTipContent'
import { CategoryToolTip } from './CategoryToolTip'

// Resolves charts dependency
charts(FusionCharts)

// ReactFusioncharts.fcRoot(FusionCharts, charts)

const MSLineComponent = (props: MSLineProps) => {
  const {
    categories,
    dataset,
    height = '40%',
    hideLegend,
    width,
    yAxisName,
    numberPrefix,
    headerLabel,
    eventsLegend,
    best
  } = props

  const [chartType, setChartType] = useState(ChartType.LINE)

  dataSource.chart.yAxisName = yAxisName ?? dataSource.chart.yAxisName
  dataSource.chart.numberPrefix = numberPrefix || undefined
  dataSource.dataset = dataset ?? dataSource.dataset
  dataSource.categories = categories ?? dataSource.categories
  const legend = Array.from(dataSource.dataset.filter((s: Series) => !s.isBest)) as Series[]

  if (best && !(dataSource.dataset?.[0] as Series)?.isBest) {
    // first in list else anchor will be above others.
    dataSource.dataset.unshift(best)
  }

  const chartContainer = useRef<ReactFusioncharts>(null)
  const { categoryToolTipProps } = useCategoryToolTipProps(chartContainer, dataSource, chartType)

  const onDataPlotRollOver = (ev) => {
    const headerValue = ev?.data?.categoryLabel
    const color = ev?.data?.anchorBgColor
    const value = ev?.data?.displayValue
    const dataIndex = ev?.data?.dataIndex
    const series: Series = ev?.sender?.originalDataSource?.dataset?.find(v =>
      v.seriesname === ev?.data?.datasetName && v.color === color)
    const label = ev?.data?.datasetName || series?.data?.[dataIndex]?.entityName
    const image = series?.image || series?.data?.[dataIndex]?.entityImage
    const headerLabel = series?.isBest
      ? 'Maximum value'
      : ev?.sender?.originalDataSource?.chart?.yAxisName

    ReactDOM.render(
      <ToolTipContent
        header={{ label: headerLabel, value: headerValue }}
        list={[
          {
            label,
            value,
            color: series?.isBest ? undefined : color,
            image
          }
        ]}
      />,
      document.getElementById('divToolTip')
    )
  }

  return (
    <div style={{ width: width ?? '100%' }}>
      <div className={styles.headContainer}>

        {hideLegend
          ? (
            <Text1 bold>
              {headerLabel}
            </Text1>
          )
          : (
            <ChartLegend
              elements={legend.map(value => ({
                label: value.seriesname,
                color: value.anchorBgColor,
                image: value.image
              }))}
            />
          )}

        <Text1 className={styles.eventsLegend}>
          {eventsLegend}
        </Text1>

        <Button
          preset='icon'
          className={cl(styles.chartTypeButton, {
            [styles.chartTypeButtonSelected]: chartType === ChartType.LINE
          })}
          onClick={() => setChartType(ChartType.LINE)}
          title='Switch to Line chart type'
        >
          <IconLineChart />
        </Button>
        <Button
          preset='icon'
          className={cl(styles.chartTypeButton, {
            [styles.chartTypeButtonSelected]: chartType === ChartType.BAR
          })}
          onClick={() => setChartType(ChartType.BAR)}
          title='Switch to Bar chart type'
        >
          <IconBarChart />
        </Button>

      </div>
      <ReactFusioncharts
        ref={chartContainer}
        type={chartType}
        width='100%'
        height={height}
        dataFormat='JSON'
        dataSource={dataSource}
        containerBackgroundColor='transparent'
        events={{
          dataPlotRollOver: onDataPlotRollOver
        }}
      />

      {categoryToolTipProps && (
        <CategoryToolTip
          {...categoryToolTipProps}
        />
      )}

    </div>
  )
}

const MSLine = MSLineComponent
export { MSLine }
